/**
 * The auto-updated copy, planned (decision 75; part 38b log, DI5, DI7, tables PU and CS): from a share's record, the
 * relay's list of its invites as read this run and the book as the exports read it, the settings each invite should
 * hold at the relay (its range's chapters, its manifest), and each item to push: a shared chapter, wrapped for the open
 * invites the relay gives it, and each such invite's manifest, wrapped for it alone. An item is pushed again only when its
 * plaintext changed or someone holds it that it wasn't wrapped for, at a version past every one the record pushed. Pure,
 * but for the hash (WebCrypto's, injected) and the version's random bytes.
 */
import { encodeInviteCopy, toHex } from '@cockatrice/core';
import { COPY_SEAL_BYTES, COPY_VERSION_BYTES } from '@cockatrice/crypto';
import type { ManifestPlace } from '@cockatrice/core';
import type { InviteRecord, PushedItem, ShareRecord } from '@cockatrice/storage';
import { toBase64Url } from '../account/bytes.ts';
import { bytesOfHex } from './secrets.ts';
import type { Random } from './secrets.ts';
import { isOpen, settingsKey } from './invite-wire.ts';
import type { InviteSettings, InviteState, OwnerInvite, OwnerList } from './invite-wire.ts';

/**
 * The book as a copy is made of it: its look as part 05 serialises it, its live chapters in order (table N), and every
 * entry's ID in the book's order, the trashed ones too (table CS: a range's ends may sit in the trash).
 */
export interface CopyBook {
  readonly metadata: string;
  readonly theme: string;
  readonly chapters: readonly { readonly id: string; readonly title: string; readonly text: string }[];
  readonly order: readonly string[];
}

/** A record's ID (hex) as the relay names it (base64url of its bytes). */
export function toBase64Id(hex: string): string {
  return toBase64Url(bytesOfHex(hex));
}

/** The most bytes a copy holds at the relay (part 38a's `MAX_COPY_BYTES`). */
export const MAX_COPY_BYTES = 1024 * 1024;

/** The longest an invite's access may run (part 38a's `MAX_INVITE_LIFE_MS`, ten years): a later end the relay refuses. */
export const MAX_INVITE_LIFE_MS = 10 * 365 * 24 * 60 * 60 * 1000;

/** The most plaintext an item holds: a copy's bytes less what sealing adds. */
export const MAX_COPY_PLAIN = MAX_COPY_BYTES - COPY_SEAL_BYTES;

/** DI5: the item of invite `id`'s manifest (`m-` and its ID's hex): apart from every chapter's ID. */
export function manifestItem(id: string): string {
  return `m-${id}`;
}

/**
 * IW5, table PU: whether `invite` is open as far as this device knows: not removed here, not over in the relay's word
 * (`state`, when listed), and its end not past by the device's clock (which only ever ends it sooner).
 */
export function inviteOpen(invite: InviteRecord, state: InviteState | undefined, now: number): boolean {
  return invite.removedAt === null && (state === undefined || isOpen(state)) && (invite.endsAt === null || invite.endsAt > now);
}

/**
 * Table CS: the chapters `invite` gives of `book`, in the book's order: all of them, or the live chapters from its range's
 * first end to its last (trashed ones count for the order); none when an end is in the book no more.
 */
export function chaptersOf(invite: Pick<InviteRecord, 'chapters'>, book: { readonly chapters: readonly { readonly id: string }[]; readonly order: readonly string[] }): readonly string[] {
  const range = invite.chapters;
  if (range === null) return book.chapters.map((chapter) => chapter.id);
  const from = book.order.indexOf(range.from);
  const to = book.order.indexOf(range.to);
  if (from < 0 || to < 0) return [];
  const span = new Set(book.order.slice(Math.min(from, to), Math.max(from, to) + 1));
  return book.chapters.filter((chapter) => span.has(chapter.id)).map((chapter) => chapter.id);
}

/** IW11, table IR: the settings `invite` should hold at the relay for `book`: its own, its chapters and its manifest. */
export function settingsOf(invite: InviteRecord, book: CopyBook): InviteSettings {
  const chapters = chaptersOf(invite, book);
  return { role: invite.role, notes: invite.notes, export: invite.export, endsAt: invite.endsAt, chapters: chapters.length === 0 ? [] : [...chapters, manifestItem(invite.id)] };
}

/** Table IR: the chapters `sent` (an `InviteRecord.sent`) gives; null for none, or a spelling not its own. */
function sentChaptersOf(sent: string | null): readonly string[] | null {
  if (sent === null) return null;
  try {
    const fields: unknown = JSON.parse(sent);
    const chapters: unknown = typeof fields === 'object' && fields !== null && 'chapters' in fields ? fields.chapters : null;
    return Array.isArray(chapters) && chapters.every((chapter) => typeof chapter === 'string') ? chapters : null;
  } catch {
    return null;
  }
}

/**
 * Fix-next 85, CS⁗ (the security audit of 2026-11-02, run 2): the earlier of two ends (null: none), so an invite is open
 * only while both this device's end and the relay's listed one are to come.
 */
export function earlierEnd(own: number | null, listed: number | null): number | null {
  if (own === null) return listed;
  return listed === null ? own : Math.min(own, listed);
}

/**
 * Fix-next 85, CS⁗: whether `listed` (a role and notes) reads no note group `own` doesn't. A note group's readers are
 * {@link readsGroup}'s (`note-plan.ts`): none without notes, else everyone's and the role's own.
 */
function readsNoMore(listed: Pick<InviteRecord, 'role' | 'notes'>, own: Pick<InviteRecord, 'role' | 'notes'>): boolean {
  return listed.notes === 'none' || (own.notes !== 'none' && listed.role === own.role);
}

/**
 * Deep-fix r10, CS‴: `invite` as the relay's `listed` entry gives it: with no change of this device's waiting (its role,
 * notes, export and end are as it last sent them), it takes the listed ones (another device's change), and its `sent`
 * with them. A change waiting here is kept, to be sent. Fix-next 85, CS⁗: the relay's list carries no proof that a
 * writer's device chose it, so it is taken only where it gives less: a role and notes reading no group this device's
 * don't, export only when off, an end only when earlier. A wider one (another device's, or a relay's forgery) stays
 * that device's own: this one never wraps keys or copies by it (accepted: its next send may narrow it again).
 */
export function adoptListed(invite: InviteRecord, listed: OwnerInvite): InviteRecord {
  const chapters = sentChaptersOf(invite.sent);
  if (chapters === null || settingsKey({ role: invite.role, notes: invite.notes, export: invite.export, endsAt: invite.endsAt, chapters }) !== invite.sent) return invite;
  const fewer = readsNoMore(listed, invite);
  const scalars = { role: fewer ? listed.role : invite.role, notes: fewer ? listed.notes : invite.notes, export: invite.export && listed.export, endsAt: earlierEnd(invite.endsAt, listed.endsAt) };
  const key = settingsKey({ ...scalars, chapters });
  return key === invite.sent ? invite : { ...invite, ...scalars, sent: key };
}

/** An item to push: what it is, its plaintext, and the open invites it is wrapped for (each once, in the record's order). */
export interface PlannedItem {
  readonly item: string;
  readonly plain: Uint8Array<ArrayBuffer>;
  readonly holders: readonly InviteRecord[];
}

/**
 * Table CS (the relay the one source of what is served; RC1, TD″): each invite of `record` the relay's list `list` holds
 * open, with the chapters of `book` it lists for it. One the list doesn't name holds nothing (unsent, or swept).
 * Deep-fix r10, CS‴: never more than chosen here: a chapter the list gives but this device's own choice doesn't (a change
 * the relay refused, another device's wider one) isn't wrapped.
 */
function holdingOf(record: ShareRecord, book: CopyBook, list: OwnerList, now: number): readonly { readonly invite: InviteRecord; readonly chapters: ReadonlySet<string> }[] {
  const listed = new Map(list.invites.map((invite) => [invite.id, invite]));
  return record.invites.flatMap((invite) => {
    const known = listed.get(toBase64Id(invite.id));
    // Deep-fix r8, CS′: open by the list, its end the listed one (moved on the other device, moved here too); fix-next 85,
    // CS⁗: and by this device's own, so the list can end an invite sooner but never keep it open longer.
    if (known === undefined || !inviteOpen({ ...invite, endsAt: earlierEnd(invite.endsAt, known.endsAt) }, known.state, now) || !known.chapters.includes(manifestItem(invite.id))) return [];
    const given = new Set(known.chapters);
    return [{ invite, chapters: new Set(chaptersOf(invite, book).filter((chapter) => given.has(chapter))) }];
  });
}

/**
 * DI5, tables PU and CS: every item of `record`'s copy of `book` at `now`, by the relay's list `list` read this run: each
 * chapter the relay gives an open invite, wrapped for those it gives it, and each such invite's manifest (the book's look
 * and its chapters, in order, and part 38e's places of its notes there, `placesOf`'s, table MP), wrapped for it alone.
 * A chapter's plaintext is `chapterPlain`'s (deep-fix r6: the caller may keep it while the chapter is the same).
 */
export function planItems(
  record: ShareRecord,
  book: CopyBook,
  list: OwnerList,
  now: number,
  placesOf: (invite: InviteRecord, chapters: ReadonlySet<string>) => readonly ManifestPlace[] = () => [],
  chapterPlain: (chapter: CopyBook['chapters'][number]) => Uint8Array<ArrayBuffer> = (chapter) => encodeInviteCopy({ kind: 'chapter', title: chapter.title, text: chapter.text }),
): readonly PlannedItem[] {
  const holding = holdingOf(record, book, list, now);
  const items: PlannedItem[] = [];
  for (const chapter of book.chapters) {
    const holders = holding.filter((held) => held.chapters.has(chapter.id)).map((held) => held.invite);
    if (holders.length > 0) items.push({ item: chapter.id, plain: chapterPlain(chapter), holders });
  }
  for (const { invite, chapters } of holding) {
    if (chapters.size === 0) continue;
    const given = book.chapters.filter((chapter) => chapters.has(chapter.id)).map((chapter) => chapter.id);
    const manifest = encodeInviteCopy({ kind: 'manifest', metadata: book.metadata, theme: book.theme, chapters: given, places: placesOf(invite, chapters) });
    items.push({ item: manifestItem(invite.id), plain: manifest, holders: [invite] });
  }
  return items;
}

/**
 * Deep-fix r3 (RC8, without planning the book again): the holders of item `item` of `book` in `record` as it stands, by
 * `list`, as {@link planItems} would name them: the invites given a chapter, or the one whose manifest it is.
 */
export function holdersOf(record: ShareRecord, book: CopyBook, list: OwnerList, now: number, item: string): readonly InviteRecord[] {
  return holdingOf(record, book, list, now)
    .filter(({ invite, chapters }) => (item === manifestItem(invite.id) ? chapters.size > 0 : chapters.has(item)))
    .map(({ invite }) => invite);
}

/** DI7: the hash an item's plaintext is pushed for: SHA-256, as hex. */
export async function itemHash(subtle: SubtleCrypto, item: PlannedItem): Promise<string> {
  return toHex(new Uint8Array(await subtle.digest('SHA-256', item.plain)));
}

/**
 * Table PU: whether `item`, of plaintext hash `hash`, needs pushing over what `pushed` holds of it: it was never pushed,
 * its plaintext changed, or someone holds it now that it wasn't wrapped for. One holding it no more needs nothing: the
 * relay drops their wraps at their removal or change (part 38a's IA9, IA17).
 */
export function needsPush(item: PlannedItem, hash: string, pushed: readonly PushedItem[]): boolean {
  const held = pushed.find((each) => each.item === item.item);
  if (held?.hash !== hash) return true;
  const wrapped = new Set(held.holders);
  return item.holders.some((invite) => !wrapped.has(invite.id));
}

/** DI7: the time in milliseconds a version's first eight bytes hold, big-endian. */
export function versionTime(version: Uint8Array): number {
  return Number(new DataView(version.buffer, version.byteOffset, version.byteLength).getBigUint64(0));
}

/** DI7: the latest time of `pushed`'s versions, or −1 for none. */
export function latestPushed(pushed: readonly PushedItem[]): number {
  return pushed.reduce((latest, item) => Math.max(latest, versionTime(bytesOfHex(item.version))), -1);
}

/**
 * DI7: a version at `time`: its eight bytes big-endian, then eight random bytes, so two devices' pushes at one millisecond
 * still differ (IN17: the later shows; a tie by its random part).
 */
export function versionAt(time: number, random: Random): Uint8Array<ArrayBuffer> {
  const version = new Uint8Array(COPY_VERSION_BYTES);
  new DataView(version.buffer).setBigUint64(0, BigInt(time));
  version.set(random(COPY_VERSION_BYTES - 8), 8);
  return version;
}

/**
 * Deep-fix r2, table PH: `pushed` once the relay gives invite `invite` the items `chapters` (with its manifest): the invite
 * out of the holders of every item it no longer holds (the relay dropped its wrap there), and an item left with no holder
 * gone (the relay dropped the copy with its last wrap), so giving the item back pushes it again.
 */
export function pruned(pushed: readonly PushedItem[], invite: string, chapters: ReadonlySet<string>): readonly PushedItem[] {
  return pushed.flatMap((entry) => {
    if (chapters.has(entry.item) || !entry.holders.includes(invite)) return [entry];
    const holders = entry.holders.filter((holder) => holder !== invite);
    return holders.length === 0 ? [] : [{ ...entry, holders }];
  });
}
